import React, { useEffect, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { db } from '../../services/firebase';
import { 
  collection, 
  query, 
  where, 
  onSnapshot,
  doc,
  getDoc,
  or,
  orderBy
} from 'firebase/firestore';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stack,
  Avatar,
  Divider,
  CircularProgress
} from '@mui/material';
import { User } from '../../types';
import MessageButton from './MessageButton';

interface Match {
  id: string;
  senderId: string;
  receiverId: string;
  message: string;
  timestamp: any;
  senderProfile?: {
    displayName: string;
    profilePicture?: string;
  };
}

interface MatchesProps {
  onSelectMatch: (matchId: string) => void;
  selectedMatchId: string | null;
}

const Matches: React.FC<MatchesProps> = ({ onSelectMatch, selectedMatchId }) => {
  const { user } = useAuth();
  const [matches, setMatches] = useState<Match[]>([]);
  const [lastMessages, setLastMessages] = useState<{[key: string]: any}>({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!user) return;

    // Fetch matches
    const receiverQuery = query(
      collection(db, 'matchRequests'),
      where('receiverId', '==', user.uid),
      where('status', '==', 'accepted')
    );

    const senderQuery = query(
      collection(db, 'matchRequests'),
      where('senderId', '==', user.uid),
      where('status', '==', 'accepted')
    );

    const unsubscribeReceiver = onSnapshot(receiverQuery, async (snapshot) => {
      handleMatchesSnapshot(snapshot);
    });

    const unsubscribeSender = onSnapshot(senderQuery, async (snapshot) => {
      handleMatchesSnapshot(snapshot);
    });

    // Fetch last messages for each match
    const messagesQuery = query(
      collection(db, 'messages'),
      or(
        where('sender', '==', user.uid),
        where('recipient', '==', user.uid)
      ),
      orderBy('timestamp', 'desc')
    );

    const unsubscribeMessages = onSnapshot(messagesQuery, (snapshot) => {
      const lastMsgs: {[key: string]: any} = {};
      
      snapshot.docs.forEach((doc) => {
        const msg = doc.data();
        const otherUserId = msg.sender === user.uid ? msg.recipient : msg.sender;
        
        if (!lastMsgs[otherUserId]) {
          lastMsgs[otherUserId] = {
            timestamp: msg.timestamp,
            message: msg.message
          };
        }
      });
      
      setLastMessages(lastMsgs);
    });

    return () => {
      unsubscribeReceiver();
      unsubscribeSender();
      if (unsubscribeMessages) unsubscribeMessages();
    };
  }, [user]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000); // 2 second delay

    return () => clearTimeout(timer);
  }, []); // Empty dependency array means this runs once on mount

  const handleMatchesSnapshot = async (snapshot: any) => {
    const matchesData: Match[] = [];
    
    for (const docSnapshot of snapshot.docs) {
      const data = docSnapshot.data() as Match;
      const otherUserId = data.senderId === user?.uid ? data.receiverId : data.senderId;
      
      const userRef = doc(db, 'users', otherUserId);
      const userDoc = await getDoc(userRef);
      const userData = userDoc.data() as User;
      
      matchesData.push({
        ...data,
        id: docSnapshot.id,
        senderProfile: {
          displayName: userData?.displayName || 'Unknown User',
          profilePicture: userData?.profilePicture
        }
      });
    }
    
    setMatches(prev => [...prev, ...matchesData]);
  };

  const sortedMatches = [...matches].sort((a, b) => {
    const aUserId = a.senderId === user?.uid ? a.receiverId : a.senderId;
    const bUserId = b.senderId === user?.uid ? b.receiverId : b.senderId;
    
    const aTimestamp = lastMessages[aUserId]?.timestamp?.toMillis() || 0;
    const bTimestamp = lastMessages[bUserId]?.timestamp?.toMillis() || 0;
    
    return bTimestamp - aTimestamp;
  });

  if (!user) return null;

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h4" gutterBottom>
        Your Matches
      </Typography>

      <Stack spacing={2}>
        {sortedMatches.length === 0 ? (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            gap: 2,
            py: 4 
          }}>
            {isLoading ? (
              <CircularProgress />
            ) : (
              <>
                <Box
                  component="img"
                  src="/nomatch.gif"
                  alt="No matches illustration"
                  sx={{
                    width: '200px',
                    height: '200px',
                    opacity: 0.7
                  }}
                />
                <Typography color="text.secondary">
                  No matches yet
                </Typography>
              </>
            )}
          </Box>
        ) : (
          sortedMatches.map((match) => {
            const otherUserId = match.senderId === user?.uid ? match.receiverId : match.senderId;
            const lastMessage = lastMessages[otherUserId];
            
            return (
              <Card 
                key={match.id}
                onClick={() => onSelectMatch(otherUserId)}
                sx={{
                  cursor: 'pointer',
                  bgcolor: selectedMatchId === otherUserId ? 'action.selected' : 'inherit'
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Avatar
                      src={match.senderProfile?.profilePicture}
                      sx={{ width: 50, height: 50, mr: 2 }}
                    />
                    <Box>
                      <Typography variant="h6">
                        {match.senderProfile?.displayName}
                      </Typography>
                      {lastMessage && (
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {lastMessage.message}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            );
          })
        )}
      </Stack>
    </Box>
  );
};

export default Matches; 